import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  Grid,
} from "@material-ui/core";
import LaunchOutlinedIcon from "@material-ui/icons/LaunchOutlined";
import React, { useState } from "react";
import clsx from "clsx";

import { useHistory } from "react-router";

import ReactJson from "react-json-view";
import { useAlert } from "react-alert";
import { useDispatch } from "react-redux";

import { FunctionTextInput } from "./FunctionTextInput";
import { FunctionDefinition } from "./types";
import { useFunctionStyles } from "./styles";
import {
  deleteFunctionDefinition,
  refreshFunctionDefinition,
  updateFunctionDefinition,
} from "../actions/functions";
import { DeleteFunctionModal } from "./DeleteFunctionModal";
import { useFunctionDefinitionValidator } from "./useFunctionDefinitionValidator";
import { RefreshFunctionModal } from "./RefreshFunctionModal";
import { RefreshIcon } from "../components/icons/themes/windows10";
import { isGitHubSetupError, GitHubSetupModal } from "./GitHubSetupModal";

interface FunctionDefViewProps {
  selectedFunctionDef: FunctionDefinition;
}

export const FunctionDefView: React.FC<FunctionDefViewProps> = ({
  selectedFunctionDef,
}) => {
  const classes = useFunctionStyles();
  const history = useHistory();
  const alert = useAlert();
  const validateFunctionDefinition = useFunctionDefinitionValidator();
  const dispatch = useDispatch();
  const [functionName, setFunctionName] = useState(selectedFunctionDef.name);
  const [description, setDescription] = useState(
    selectedFunctionDef.description
  );
  const [deleteFunctionModalOpen, setDeleteFunctionModalOpen] = useState(false);
  const [refreshFunctionModalOpen, setRefreshFunctionModalOpen] = useState(
    false
  );
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [gitHubSetupError, setGitHubSetupError] = useState<string | null>(null);

  const onSave = () => {
    const updatedDefinition = {
      ...selectedFunctionDef,
      name: functionName,
      description: description,
    };

    const validationError = validateFunctionDefinition(updatedDefinition);

    if (validationError) {
      alert.error(validationError.message);
      return;
    }

    (dispatch(updateFunctionDefinition(updatedDefinition)) as any)
      .then(() => {
        alert.success("Function Saved");
      })
      .catch((err: any) => {
        if (err) {
          if (isGitHubSetupError(err.message)) {
            setGitHubSetupError(err.message);
          } else {
            alert.error("Could not save function");
            console.log(err);
          }
        }
      });
  };

  const onDelete = () => {
    (dispatch(deleteFunctionDefinition(selectedFunctionDef.id)) as any).then(
      () => {
        alert.success("Function Deleted");
        history.replace("/functions");
      }
    );
  };

  const onRefresh = () => {
    setIsRefreshing(true);
    (dispatch(refreshFunctionDefinition(selectedFunctionDef.id)) as any)
      .then((resp: any) => {
        console.log("got response on refresh", resp);
        alert.success("Function Refreshed");
      })
      .catch((err: any) => {
        if (err) {
          if (isGitHubSetupError(err.message)) {
            setGitHubSetupError(err.message);
          } else {
            alert.error("Could not refresh the function");
            console.log(err);
          }
        }
      })
      .finally(() => {
        setIsRefreshing(false);
      });
  };

  return (
    <>
      <Grid
        container
        spacing={0}
        alignItems="flex-start"
        alignContent="flex-start"
        className={clsx(classes.widgets, "px-5 pb-2")}
      >
        <Grid item xs={11}>
          <div className={classes.widget}>
            <Card>
              <CardHeader
                title={selectedFunctionDef.name}
                classes={{ action: clsx(classes.closeIconAction) }}
                action={
                  <Button
                    variant="outlined"
                    onClick={() => history.push("/functions")}
                  >
                    Back
                  </Button>
                }
              />
              <Divider />
              <CardContent>
                <div>
                  {/* :TODO: (jmtaber129): Make this prettier. */}
                  Function Type: {selectedFunctionDef.type}
                </div>
                <div className={classes.link}>
                  <LaunchOutlinedIcon className={classes.linkIcon} />
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href={selectedFunctionDef.source}
                  >
                    {selectedFunctionDef.source}
                  </a>
                </div>
                <FunctionTextInput
                  label="Name"
                  key="name"
                  name="name"
                  value={functionName}
                  onChangeText={setFunctionName}
                />
                <FunctionTextInput
                  label="Description"
                  key="description"
                  name="description"
                  value={description}
                  onChangeText={setDescription}
                  multiline
                />
                <div>
                  Config:
                  <ReactJson
                    src={selectedFunctionDef.config || {}}
                    indentWidth={2}
                    collapsed={2}
                    displayDataTypes={false}
                    name={null}
                    groupArraysAfterLength={5}
                  />
                </div>
              </CardContent>
              <Divider />
              <CardActions className={classes.actions}>
                {isRefreshing && (
                  <RefreshIcon className={clsx("animate-spin")} />
                )}
                <Button
                  color="primary"
                  variant="outlined"
                  onClick={() => setRefreshFunctionModalOpen(true)}
                  disabled={isRefreshing}
                >
                  {!isRefreshing ? "Refresh" : "Refreshing..."}
                </Button>

                <Button
                  className={clsx(classes.deleteFunctionButton)}
                  onClick={() => setDeleteFunctionModalOpen(true)}
                  disabled={isRefreshing}
                >
                  Delete
                </Button>
                <Button
                  className={clsx(classes.createFunctionButton)}
                  onClick={onSave}
                  variant="outlined"
                  disabled={isRefreshing}
                >
                  SAVE
                </Button>
              </CardActions>
            </Card>
          </div>
        </Grid>
      </Grid>
      <DeleteFunctionModal
        open={deleteFunctionModalOpen}
        onClose={() => setDeleteFunctionModalOpen(false)}
        onConfirm={onDelete}
      />
      <RefreshFunctionModal
        open={refreshFunctionModalOpen}
        onClose={() => setRefreshFunctionModalOpen(false)}
        onConfirm={onRefresh}
      />
      <GitHubSetupModal
        gitHubError={gitHubSetupError}
        onClose={() => setGitHubSetupError(null)}
      />
    </>
  );
};
